// Certified Materials — pure RPC layer (ported from old
// `views/certified-materials/api/materials-api.ts`).
//
// All calls hit the dedicated certified-materials service and go through the
// shared `apiFetch` wrapper (auth headers, error handling, 401 redirect).

import {
  API_ROOT_URL,
  CERTIFIED_MATERIALS_SERVICE_URL,
} from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
// CMM_COMPAT: unwraps platform's envelope, or passes CMM's bare payload through.
import { cmmData } from './compat';
import type {
  IApiDocument,
  IApiEnvelope,
  IApiFieldMatch,
  IApiOrder,
  IApiOrderChangesSinceCheck,
  IApiOrdersResponse,
  IApiProduct,
  IAssignee,
  IBulkSellerMappingBody,
  IBulkSellerMappingResult,
  IBulkUpdateOrdersPatch,
  IBulkUpdateOrdersResponse,
  IConfirmDocumentsReviewBody,
  IConfirmEvidenceResponse,
  IDuplicateDocumentResponse,
  IEvidenceItem,
  IFilterOptionRaw,
  IImportJob,
  IImportOrdersBody,
  IImportOrdersResponse,
  INetworkCompaniesResponse,
  IPlatformCompanyRow,
  IPlatformEnvelope,
  IPlatformPaginatedEnvelope,
  ISellerMappingInput,
  ISellerMappingResult,
  ISendRemindersBody,
  ISendRequestEntry,
  IUnmappedSellersResult,
  IUpdateDocumentBody,
  IUpdateOrderBody,
  IUploadDocBody,
  IUploadOrderDocBody,
} from './types';

const BASE = CERTIFIED_MATERIALS_SERVICE_URL;

// NOTE on response shapes (verified against old `@lib/fetch` usage):
//  - orders list is migrated to platform: `{ metadata: { success, pagination },
//    data: [...] }` (IApiOrdersResponse). `pagination` used to be top level.
//  - update endpoints still return the order object directly (unmigrated).
//  - filter-options endpoints ARE wrapped: `{ data: [...] }` (IApiEnvelope).
// The shared `apiFetch` returns the raw body (unlike the old fetch wrapper that
// added a `{ data, error, status }` envelope), so we type each call to its real
// body shape.

/**
 * Migrated to platform. The envelope is *not* unwrapped here, unlike
 * `fetchOrderById`: the caller needs the pagination block as well as the rows,
 * so it reads `res.metadata.pagination` and `res.data`.
 */
export function fetchOrdersList(qs: string): Promise<IApiOrdersResponse> {
  return apiFetch<IApiOrdersResponse>(`/orders${qs}`, {}, BASE);
}

/**
 * Migrated to platform, which wraps responses as `{ metadata, data }` — unlike
 * the CMM-era endpoints around it, which return their payload bare. Unwrapped
 * here so `useOrderDetailQuery` and every other consumer keep seeing an
 * `IApiOrder`.
 *
 * `updateOrder` / `updateOrderStatus` below PATCH the same path and are also
 * migrated, so they unwrap the same way.
 */
export async function fetchOrderById(orderId: string): Promise<IApiOrder> {
  const res = await apiFetch<IPlatformEnvelope<IApiOrder>>(
    `/orders/${orderId}`,
    {},
    BASE,
  );
  return cmmData(res);
}

/**
 * Migrated to platform. No caller reads the returned order — both mutations
 * patch the cache optimistically — but the envelope is unwrapped here anyway so
 * the declared `IApiOrder` stays true for whoever reads it next.
 */
export async function updateOrder(
  orderId: string,
  body: IUpdateOrderBody,
): Promise<IApiOrder> {
  const res = await apiFetch<IPlatformEnvelope<IApiOrder>>(
    `/orders/${orderId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    BASE,
  );
  return cmmData(res);
}

export async function updateOrderStatus(
  orderId: string,
  status: string,
): Promise<IApiOrder> {
  const res = await apiFetch<IPlatformEnvelope<IApiOrder>>(
    `/orders/${orderId}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
    BASE,
  );
  return cmmData(res);
}

// Imports a PO spreadsheet, parsed and persisted server-side by a background
// job; answers 202 with an importId to poll. Migrated to platform, which wraps
// the object in the standard envelope; unwrapped here so callers are unchanged.
export async function importOrders(
  body: IImportOrdersBody,
): Promise<IImportOrdersResponse> {
  const res = await apiFetch<IPlatformEnvelope<IImportOrdersResponse>>(
    '/orders',
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
  return cmmData(res);
}

// Polls one async import job's status/result. Migrated to platform, which wraps
// the object in the standard envelope; unwrapped here so callers are unchanged.
export async function fetchImportJob(importId: string): Promise<IImportJob> {
  const res = await apiFetch<IPlatformEnvelope<IImportJob>>(
    `/orders/import-jobs/${importId}`,
    {},
    BASE,
  );
  return cmmData(res);
}

// Uploads an evidence document. Server classifies + matches it to orders and
// starts AI processing. Returns 409 CONFLICT for duplicate files.
export function uploadDocument(body: IUploadDocBody): Promise<unknown> {
  return apiFetch<unknown>(
    '/documents',
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
}

// Dedup check: the still-active document already stored with this MD5 hash, plus
// the orders it is linked to (only when it belongs to the requesting company).
// `qs` must be a pre-built query string, e.g. `?filter=...` (built in the hook layer).
export async function fetchDuplicateDocument(
  qs: string,
): Promise<IDuplicateDocumentResponse> {
  // Migrated: platform nests CMM's four top-level keys under the envelope's
  // `data`. Unwrapped here so consumers keep reading `res.data` / `res.orders`.
  const res = await apiFetch<IPlatformEnvelope<IDuplicateDocumentResponse>>(
    `/documents/duplicates${qs}`,
    {},
    BASE,
  );
  return cmmData(res);
}

// Documents awaiting human review (drives the Review Docs button count AND the
// list rendered inside the review dialog).
// `qs` must be a pre-built query string (built in the hook layer).
export function fetchDocumentsPendingReview(
  qs: string,
): Promise<{ data: IApiDocument[] }> {
  return apiFetch<{ data: IApiDocument[] }>(`/documents${qs}`, {}, BASE);
}

// One page of orders for the review dialog's "linked orders" picker. Same
// migrated `GET /orders` as `fetchOrdersList`, so it shares that envelope; the
// caller builds the query string (see `useLinkableOrdersSelect`).
export function fetchAvailableOrders(qs: string): Promise<IApiOrdersResponse> {
  return apiFetch<IApiOrdersResponse>(`/orders${qs}`, {}, BASE);
}

// Confirms reviewed documents (type + linked orders + extraction edits).
export function confirmDocumentsReview(
  body: IConfirmDocumentsReviewBody,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/documents/confirm',
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
}

// Bulk "send request" — one document_submission request per supplier. The
// service takes the entries array directly as the body.
export function sendBulkRequests(
  entries: ISendRequestEntry[],
): Promise<unknown> {
  return apiFetch<unknown>(
    '/requests',
    { method: 'POST', body: JSON.stringify(entries) },
    BASE,
  );
}

// Bulk "send reminder" — one reminder per pending request id.
export function sendBulkReminders(body: ISendRemindersBody): Promise<unknown> {
  return apiFetch<unknown>(
    '/requests/remind',
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
}

// Bulk-applies one field change (status / due date / custom tags)
// to many orders in a single request. `patch` carries exactly one field — see
// `IBulkUpdateOrdersPatch` for the clear semantics per field.
export async function bulkUpdateOrders(
  ids: string[],
  patch: IBulkUpdateOrdersPatch,
): Promise<IBulkUpdateOrdersResponse> {
  const res = await apiFetch<IPlatformEnvelope<IBulkUpdateOrdersResponse>>(
    '/orders/bulk',
    { method: 'PATCH', body: JSON.stringify({ ids, patch }) },
    BASE,
  );
  return cmmData(res);
}

/**
 * Migrated to platform, so the body is now `{ metadata, data }` carrying
 * `{ deletedCount, requestedCount }`. Nothing reads it — the caller toasts
 * `ids.length` — so this stays `void` rather than unwrapping a count no one
 * consumes.
 */
export function deleteOrders(ids: string[]): Promise<void> {
  return apiFetch<void>(
    '/orders',
    { method: 'DELETE', body: JSON.stringify({ ids }) },
    BASE,
  );
}

// ── Filter options ──────────────────────────────────────────────────────────

function fetchFilterOptions(
  path: string,
): Promise<IApiEnvelope<IFilterOptionRaw[]>> {
  return apiFetch<IApiEnvelope<IFilterOptionRaw[]>>(
    `/filter-options/${path}`,
    {},
    BASE,
  );
}

// Products for the article-number dropdown filter. Not under `/filter-options/*`
// — the products list lives at `/products` on the cert-materials service.
export function fetchProducts(): Promise<IApiEnvelope<IApiProduct[]>> {
  return apiFetch<IApiEnvelope<IApiProduct[]>>('/products', {}, BASE);
}

// ── Order-details drawer endpoints ──────────────────────────────────────────

/**
 * Migrated to platform, which wraps the array as `{ metadata, data }` — CMM
 * returned it bare. Unwrapped here so `useOrderDocumentCheck` and the Document
 * Content Check section keep seeing a plain array.
 */
export async function fetchOrderDocumentCheck(
  orderId: string,
): Promise<IApiFieldMatch[]> {
  const res = await apiFetch<IPlatformEnvelope<IApiFieldMatch[]>>(
    `/orders/${orderId}/field-matches`,
    {},
    BASE,
  );
  return cmmData(res);
}

// Re-runs field matching for every document linked to the order. Needed before
// re-reading `/field-matches`, which only returns rows that already exist: a
// document whose match was never written (extraction failed, or it was linked
// before matching ran) is listed under Evidence but stays invisible in Document
// Content Check no matter how often the section is refetched.
export async function rematchOrderDocuments(
  orderId: string,
): Promise<IApiFieldMatch[]> {
  const res = await apiFetch<IPlatformEnvelope<IApiFieldMatch[]>>(
    `/orders/${orderId}/field-match`,
    { method: 'POST' },
    BASE,
  );
  return cmmData(res);
}

// Platform-only (no CMM counterpart): what moved in the Order Hub since the
// order's content check ran. Returns the envelope; the hook unwraps it.
export function fetchOrderChangesSinceCheck(
  orderId: string,
): Promise<IPlatformEnvelope<IApiOrderChangesSinceCheck>> {
  return apiFetch<IPlatformEnvelope<IApiOrderChangesSinceCheck>>(
    `/orders/${orderId}/changes-since-check`,
    {},
    BASE,
  );
}

// Migrated to platform, which wraps the array in the standard envelope where
// CMM returned it bare; unwrapped here so the hook is unchanged.
export async function fetchOrderEvidenceItems(
  orderId: string,
): Promise<IEvidenceItem[]> {
  const res = await apiFetch<IPlatformEnvelope<IEvidenceItem[]>>(
    `/orders/${orderId}/evidence-items`,
    {},
    BASE,
  );
  return cmmData(res);
}

// Migrated to platform, which wraps the object in the standard envelope;
// unwrapped here so the mutation hook is unchanged.
export async function confirmEvidence(
  orderId: string,
  evidenceId: string,
  confirmed: boolean,
): Promise<IConfirmEvidenceResponse> {
  const res = await apiFetch<IPlatformEnvelope<IConfirmEvidenceResponse>>(
    `/orders/${orderId}/evidence-items/${evidenceId}/confirm`,
    { method: 'PATCH', body: JSON.stringify({ confirmed }) },
    BASE,
  );
  return cmmData(res);
}

export function uploadOrderDocument(
  orderId: string,
  body: IUploadOrderDocBody,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/orders/${orderId}/documents`,
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
}

export function deleteDocument(documentId: string): Promise<void> {
  return apiFetch<void>(`/documents/${documentId}`, { method: 'DELETE' }, BASE);
}

// Migrated: GET /documents/:id now answers with the platform envelope, so the
// unwrap lives here and every consumer stays unchanged.
export async function fetchDocumentById(
  documentId: string,
): Promise<IApiDocument> {
  const res = await apiFetch<IPlatformEnvelope<IApiDocument>>(
    `/documents/${documentId}`,
    {},
    BASE,
  );
  return cmmData(res);
}

// Update a document's extracted-data certificate block (PATCH /documents/:id).
// `code` is the certificate identifier (tcNumber / invoiceNumber / shippingDocNumber).
export function updateDocument(
  documentId: string,
  body: IUpdateDocumentBody,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/documents/${documentId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    BASE,
  );
}

export function requestDocumentReupload(
  documentId: string,
  message: string,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/requests',
    {
      method: 'POST',
      body: JSON.stringify([
        { type: 'document_revision', documentIds: [documentId], message },
      ]),
    },
    BASE,
  );
}

// Migrated to platform. No caller reads the returned match — the section
// refetches — but the envelope is unwrapped anyway so the resolved value stays
// the field match rather than a wrapper.
export async function validateFieldMatch(
  matchId: string,
  fieldName: string,
): Promise<IApiFieldMatch> {
  const res = await apiFetch<IPlatformEnvelope<IApiFieldMatch>>(
    `/field-matches/${matchId}/manual-validations`,
    { method: 'POST', body: JSON.stringify({ fieldName }) },
    BASE,
  );
  return cmmData(res);
}

export async function revokeFieldMatchValidation(
  matchId: string,
  fieldName: string,
): Promise<IApiFieldMatch> {
  const res = await apiFetch<IPlatformEnvelope<IApiFieldMatch>>(
    `/field-matches/${matchId}/manual-validations/${encodeURIComponent(fieldName)}`,
    { method: 'PATCH', body: JSON.stringify({ action: 'revoke' }) },
    BASE,
  );
  return cmmData(res);
}

export function fetchUsersByCompany(
  companyId: string,
): Promise<IApiEnvelope<IAssignee[]> | IAssignee[]> {
  return apiFetch<IApiEnvelope<IAssignee[]> | IAssignee[]>(
    `/users?companyId=${encodeURIComponent(companyId)}&limit=100`,
    {},
    BASE,
  );
}

// Platform standards (GOTS, GRS, ...) — the list used for order-line standards,
// from the platform catalogue (NOT the certified-materials filter-options).
export function fetchPlatformStandards(): Promise<{
  data: Array<{ id: string; name: string; shortName?: string | null }>;
}> {
  return apiFetch('/api/standards?count=100', {}, API_ROOT_URL);
}

// ── Seller → network-company mapping ─────────────────────────────────────────

// Buyer-scoped network companies for the map picker. `qs` is a pre-built query
// string (search + page + limit), assembled in the hook layer.
//
// **Reused, not migrated.** Platform's own `GET /api/companies` already serves
// this over the same tables, so it is called on platform's base and is
// deliberately unaffected by the certified-materials cutover flip. The whole
// surface delta is absorbed here; the hook is unchanged. See
// backend/src/api/routes/certified-materials/docs/migration-status.md.
export async function fetchCompanies(
  qs: string,
): Promise<INetworkCompaniesResponse> {
  const cmm = new URLSearchParams(qs.replace(/^\?/, ''));
  const search = cmm.get('search') ?? '';
  const limit = Number(cmm.get('limit') ?? 20);
  // CMM pages from 1, platform from 0.
  const page = Number(cmm.get('page') ?? 1) - 1;

  const params = new URLSearchParams({
    page: String(page),
    count: String(limit),
  });
  if (search) {
    // `$or` keeps CMM's "name OR internal code" search; a plain `name` filter
    // would silently drop code lookups, which is what this picker is for.
    params.set(
      'filter',
      JSON.stringify({ $or: { name: search, internalCompanyCode: search } }),
    );
  }

  // `API_ROOT_URL`, not `API_BASE_URL`: these routes are mounted at
  // `<root>/api/`, while `API_BASE_URL` already ends in `/api-service` and
  // would resolve to `/api-service/api/companies`, which serves nothing.
  const res = await apiFetch<IPlatformPaginatedEnvelope<IPlatformCompanyRow>>(
    `/api/companies?${params.toString()}`,
    {},
    API_ROOT_URL,
  );

  return {
    data: res.data.map(row => ({
      retracedId: row.id,
      internalId: row.internalCompanyCode ?? null,
      name: row.name ?? '',
    })),
    pagination: {
      page: res.meta.page + 1,
      limit: res.meta.pageSize,
      total: res.meta.totalCount,
      totalPages: Math.ceil(res.meta.totalCount / (res.meta.pageSize || 1)),
    },
  };
}

// Maps one seller internal code to a network company; back-fills seller_te_id
// on the buyer's orders carrying that code.
// Migrated to platform; the envelope is unwrapped here so the hooks are unchanged.
export async function mapSellerCompany(
  body: ISellerMappingInput,
): Promise<ISellerMappingResult> {
  const res = await apiFetch<IPlatformEnvelope<ISellerMappingResult>>(
    '/companies/seller-mappings',
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
  return cmmData(res);
}

// The buyer's distinct unmapped sellers (+ counts) for the banner + bulk dialog.
// Migrated to platform; the envelope is unwrapped here so the hooks are unchanged.
export async function fetchUnmappedSellers(): Promise<IUnmappedSellersResult> {
  const res = await apiFetch<IPlatformEnvelope<IUnmappedSellersResult>>(
    '/companies/unmapped-sellers',
    {},
    BASE,
  );
  return cmmData(res);
}

// Maps several sellers to network companies in one request.
// Migrated to platform, which wraps the object in the standard envelope;
// unwrapped here so the mutation hook is unchanged.
export async function bulkMapSellerCompanies(
  body: IBulkSellerMappingBody,
): Promise<IBulkSellerMappingResult> {
  const res = await apiFetch<IPlatformEnvelope<IBulkSellerMappingResult>>(
    '/companies/seller-mappings/bulk',
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
  return cmmData(res);
}

export const fetchStandardsOptions = () => fetchFilterOptions('standards');
// Legacy slug — `vendors` returns distinct SELLER names, which is why it backs
// the Seller Name filter. Real vendor names come from `vendor-names`.
export const fetchVendorsOptions = () => fetchFilterOptions('vendors');
export const fetchVendorNamesOptions = () => fetchFilterOptions('vendor-names');
// Buying companies visible to the caller — keyed by company id, not name.
export const fetchBuyersOptions = () => fetchFilterOptions('buyers');
export const fetchSeasonsOptions = () => fetchFilterOptions('seasons');
export const fetchCustomTagsOptions = () => fetchFilterOptions('custom-tags');
export const fetchSupplierAssigneesOptions = () =>
  fetchFilterOptions('supplier-assignees');
export const fetchBrandsOptions = () => fetchFilterOptions('brands');
export const fetchResponsiblePersonsOptions = () =>
  fetchFilterOptions('responsible-persons');
